/**
 * `<LinkFromScreen>` — the builder's **Link from screen…**, in the Columns tab and
 * the Conditions tab (`docs/plans/screen-links.md`, the picker mock).
 *
 *     [+ Column] [+ Logo] [Link from screen… ▾]
 *                          JAS · Impulse
 *                          JP  · Persons          ▸
 *                          ☑ PPS Trend  ☑ LCC Trend  ☐ HCD/LCD  ✓ on screen
 *                          [Link 2 columns]
 *
 * `<CopyFromScreen>`'s shape and its seam — the host lists the screens and loads
 * one; the link itself is `lib/screen-links.ts` — with two differences that are
 * the point of linking. It is offered to **every member**, for the screens of
 * groups they hold; and a sealed condition **can** be picked, because a link
 * carries no rule — it is evaluated on the server from its own screen.
 */

import { useEffect, useRef, useState } from "react";
import type { SavedScreenerFilter, ScreenerColumn } from "@qufolio/model";

import { copySourcesByOwner, type CopySourceDefinition, type CopySourceScreen } from "../lib/copy-from-screen.js";
import { isSealed } from "../lib/saved-filters.js";
import { usePopoverPlace } from "./popover-place.js";
import type { ScreenLinks } from "../lib/screen-links.js";

type Props =
  | {
      kind: "columns";
      source: ScreenLinks;
      /**
       * A field's band (`Price`, `Valuation`, `JP` …) — how the picker tells a
       * group's own columns from the common ones anyone can tick.
       */
      categoryOf?: (field: string) => string | undefined;
      /** The screen being edited — never offered as its own source. */
      current: string | undefined;
      have: readonly ScreenerColumn[];
      /** The picked source columns and the screen they are linked from. */
      onLink: (picked: ScreenerColumn[], screen: string) => void;
    }
  | {
      kind: "conditions";
      source: ScreenLinks;
      current: string | undefined;
      have: readonly SavedScreenerFilter[];
      onLink: (picked: SavedScreenerFilter[], screen: string) => void;
    };

interface Item {
  id: string;
  label: string;
  present: boolean;
  sealed?: boolean;
  /** The source's own link — a link names one screen, never a chain. */
  linked?: boolean;
}

export function LinkFromScreen(props: Props) {
  const { kind, source, current } = props;
  const [open, setOpen] = useState(false);
  // A press anywhere else closes it — so it and its neighbour (Copy / Link)
  // cannot both stand open on top of each other (owner, 2026-10-16).
  const rootRef = useRef<HTMLSpanElement | null>(null);
  // Conditions are long names (*Person Weekly Volume Trend Up*): two wider
  // columns that wrap, and a taller list, so each reads in full (owner,
  // 2026-10-19). Columns keep three — their headers are short.
  const place = usePopoverPlace(open, rootRef, kind === "conditions" ? 560 : 460, kind === "conditions" ? 520 : 420);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  const [from, setFrom] = useState<CopySourceScreen | null>(null);
  const [loaded, setLoaded] = useState<CopySourceDefinition | null | "loading">(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());

  const groups = copySourcesByOwner(source.screens, current);
  const noun = kind === "columns" ? "column" : "condition";
  // Common columns are hidden unless asked for (owner, 2026-10-16).
  const [showAll, setShowAll] = useState(false);

  const pick = (screen: CopySourceScreen) => {
    setFrom(screen);
    setTicked(new Set());
    setLoaded("loading");
    void source.load(screen.key).then(
      (definition) => setLoaded(definition),
      () => setLoaded(null),
    );
  };

  const items: Item[] =
    loaded && loaded !== "loading"
      ? kind === "columns"
        ? (loaded.columns ?? [])
            .filter((c) => showAll || !from || ownColumn(c, from.ownerName, "categoryOf" in props ? props.categoryOf : undefined))
            .map((c) => ({
            id: c.field,
            label: c.header || c.field,
            present: props.have.some((h) => h.field === c.field),
            ...(c.link ? { linked: true } : {}),
          }))
        : (loaded.savedFilters ?? []).map((f) => ({
            id: f.id,
            label: f.name,
            present: (props.have as readonly SavedScreenerFilter[]).some(
              (h) => h.id === f.id || h.link?.id === f.id || h.name.trim() === f.name.trim(),
            ),
            ...(isSealed(f) ? { sealed: true } : {}),
            ...(f.link ? { linked: true } : {}),
          }))
      : [];
  const linkable = (i: Item) => !i.present && !i.linked;
  const count = items.filter((i) => linkable(i) && ticked.has(i.id)).length;

  const link = () => {
    if (!loaded || loaded === "loading" || !from) return;
    const chosen = new Set(items.filter((i) => linkable(i) && ticked.has(i.id)).map((i) => i.id));
    if (props.kind === "columns") {
      props.onLink((loaded.columns ?? []).filter((c) => chosen.has(c.field)), from.key);
    } else {
      props.onLink((loaded.savedFilters ?? []).filter((f) => chosen.has(f.id)), from.key);
    }
    setOpen(false);
    setFrom(null);
    setLoaded(null);
    setTicked(new Set());
  };

  return (
    <span ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        title={`Link ${noun}s from a screen of a group you belong to — they stay in step with it`}
        style={BUTTON}
      >
        Link from screen… ▾
      </button>
      {open && (
        <div role="dialog" aria-label="Link from screen" style={{ ...POPOVER, ...place }}>
          {groups.length === 0 && <div style={NOTE}>No group screen to link from.</div>}
          {/* Once a screen is picked the list collapses to it, so the screens
              and their checklist never stack into one long column. */}
          {from && (
            <button type="button" onClick={() => { setFrom(null); setLoaded(null); setTicked(new Set()); }}
              style={{ ...ROW_BUTTON, color: "var(--qf-accent)" }}>
              ‹ Other screens
            </button>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {groups.flatMap(({ owner, screens }) =>
              screens.filter((screen) => !from || from.key === screen.key).map((screen) => (
                <button
                  key={screen.key}
                  type="button"
                  onClick={() => pick(screen)}
                  style={{
                    ...ROW_BUTTON,
                    ...(from?.key === screen.key
                      ? { background: "var(--qf-selection)", color: "var(--qf-text)" }
                      : {}),
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {owner} · {screen.name}
                  </span>
                  {from?.key === screen.key && <span aria-hidden="true">▸</span>}
                </button>
              )),
            )}
          </div>
          {loaded === "loading" && <div style={NOTE}>Loading…</div>}
          {from && loaded === null && <div style={NOTE}>That screen could not be read.</div>}
          {from && loaded && loaded !== "loading" && (
            <>
              {items.length === 0 && <div style={NOTE}>It has no {noun}s.</div>}
              {kind === "columns" && (
                <label style={{ ...ROW, marginTop: 6 }} title="Price, fundamentals and general indicators — the columns you can tick yourself">
                  <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                  Show common columns too
                </label>
              )}
              {items.some(linkable) && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, fontSize: "0.74rem" }}>
                  <button type="button" style={BUTTON}
                    onClick={() => setTicked(new Set(items.filter(linkable).map((i) => i.id)))}>
                    Select all ({items.filter(linkable).length})
                  </button>
                  <button type="button" style={BUTTON} disabled={count === 0} onClick={() => setTicked(new Set())}>
                    Clear
                  </button>
                </div>
              )}
              <div style={kind === "conditions" ? CONDITION_LIST : LIST}>
                {items.map((item) => (
                  <label
                    key={item.id}
                    style={{ ...ROW, ...(!linkable(item) ? { color: "var(--qf-text-muted)" } : {}) }}
                    title={
                      item.present
                        ? "Already on this screen"
                        : item.linked
                          ? `Linked into ${from.name} from another screen — link it from there`
                          : item.sealed
                            ? `${from.ownerName}'s rule — applied on the server`
                            : undefined
                    }
                  >
                    <input
                      type="checkbox"
                      disabled={!linkable(item)}
                      checked={linkable(item) && ticked.has(item.id)}
                      onChange={(e) =>
                        setTicked((had) => {
                          const next = new Set(had);
                          if (e.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        })
                      }
                    />
                    <span
                      style={
                        kind === "conditions"
                          ? { overflowWrap: "anywhere" }
                          : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                      }
                    >
                      {item.label}
                    </span>
                    {item.sealed && " 🔒"}
                    {item.present && <span style={{ fontSize: "0.7rem" }}> ✓ on screen</span>}
                  </label>
                ))}
              </div>
              <button type="button" disabled={count === 0} onClick={link} style={PRIMARY}>
                Link {count} {noun}
                {count === 1 ? "" : "s"}
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
}

/** `CopyFromScreen`'s, so the two sit side by side as one family. */
const BUTTON: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.05rem 0.35rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  fontSize: "0.74rem",
};

const ROW_BUTTON: React.CSSProperties = {
  ...BUTTON,
  display: "flex",
  alignItems: "center",
  width: "100%",
  textAlign: "left",
  border: "none",
  padding: "0.15rem 0.35rem",
};

const PRIMARY: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.2rem 0.55rem",
  marginTop: 6,
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.76rem",
};

const POPOVER: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  zIndex: 20,
  marginTop: 4,
  width: 460,
  maxWidth: "90vw",
  padding: 8,
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
};

/** Three tidy columns (owner, 2026-10-16) rather than a wrapping run. */
const LIST: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "2px 10px",
  marginTop: 6,
  maxHeight: 260,
  overflowY: "auto",
};

const CONDITION_LIST: React.CSSProperties = {
  ...LIST,
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "3px 14px",
  maxHeight: 380,
};

const ROW: React.CSSProperties = {
  minWidth: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.76rem",
};

const NOTE: React.CSSProperties = {
  marginTop: 6,
  fontSize: "0.72rem",
  color: "var(--qf-text-muted)",
};

/**
 * A source screen's **own** column — worth linking — rather than a common one
 * anyone can tick from their own Columns list: a formula the screen wrote, or a
 * column in its group's band (`JP` on a JP screen), or one whose band is unknown
 * here. Price, fundamentals and the general indicator bands are common. With no
 * categories to go on, everything counts, so nothing is hidden by mistake.
 */
export function ownColumn(
  column: { field: string; computed?: unknown },
  ownerName: string | undefined,
  categoryOf: ((field: string) => string | undefined) | undefined,
): boolean {
  if (column.computed) return true;
  if (!categoryOf || !ownerName) return true;
  const band = categoryOf(column.field);
  // Unknown here is not "common": another group's column may simply not be in
  // this panel's own list — shown rather than hidden by mistake.
  return band === undefined || band === ownerName;
}
